import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CachesService } from '../../../services/caches/caches.service';
import { Cache } from '../../../models/cache';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'lib-manage-caches',
    templateUrl: './manage-caches.component.html',
    styleUrls: ['./manage-caches.component.css'],
    standalone: false
})
export class ManageCachesComponent implements OnInit, OnDestroy {
  protected cachesService = inject(CachesService);
  cachesList: Cache[] = [];

  keysShownMap: { [cacheName: string]: boolean } = {};
  keysLoadingMap: { [cacheName: string]: boolean } = {};
  cacheKeysMap: { [cacheName: string]: string[] } = {};
  loadingSize = false;

  unsubscribe: Subject<boolean> = new Subject<boolean>();


  ngOnInit() {
    this.loadCache();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  loadCache() {
    this.loadingSize = true;
    this.cachesService
      .getCaches()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(caches => {
        this.cachesList = caches.sort((a, b) => a.name.localeCompare(b.name));
        // Check if legacy backend already returns size data
        const hasSizeData = caches.some(c => c.size != null && c.size > 0);
        if (hasSizeData) {
          this.loadingSize = false;
        } else {
          this.loadCachesWithSize();
        }
      });
  }

  loadCachesWithSize() {
    this.cachesService
      .getCaches(true)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(caches => {
        const sizeMap = new Map(caches.map(c => [c.name, c.size]));
        this.cachesList = this.cachesList.map(cache => ({
          ...cache,
          size: sizeMap.get(cache.name) ?? cache.size
        }));
        this.loadingSize = false;
      });
  }

  clearCache(name: string) {
    this.loadingSize = true;
    this.cachesService
      .getClearCache(name)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(caches => {
        this.cachesList = caches.sort((a, b) => a.name.localeCompare(b.name));
        // Clear cached keys for this cache
        delete this.cacheKeysMap[name];
        this.keysShownMap[name] = false;
        // Reload sizes
        this.loadCachesWithSize();
      });
  }

  clearAllCache() {
    this.loadingSize = true;
    this.cachesService
      .getClearAllCache()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(caches => {
        this.cachesList = caches.sort((a, b) => a.name.localeCompare(b.name));
        // Clear all cached keys
        this.cacheKeysMap = {};
        this.keysShownMap = {};
        // Reload sizes
        this.loadCachesWithSize();
      });
  }

  deleteCacheKey(cacheName: string, key: string) {
    this.loadingSize = true;
    this.cachesService
      .getClearCacheKey(cacheName, key)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(caches => {
        this.cachesList = caches.sort((a, b) => a.name.localeCompare(b.name));
        // Remove key from local cache
        if (this.cacheKeysMap[cacheName]) {
          this.cacheKeysMap[cacheName] = this.cacheKeysMap[cacheName].filter(k => k !== key);
        }
        // Reload sizes
        this.loadCachesWithSize();
      });
  }

  toggleKeysVisibility(cacheName: string) {
    if (this.keysShownMap[cacheName]) {
      this.keysShownMap[cacheName] = false;
      return;
    }

    // Fetch keys if not already loaded
    if (!this.cacheKeysMap[cacheName]) {
      this.loadCacheKeys(cacheName);
    } else {
      this.keysShownMap[cacheName] = true;
    }
  }

  loadCacheKeys(cacheName: string) {
    this.keysLoadingMap[cacheName] = true;
    this.cachesService
      .getCacheInfo(cacheName)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (cache) => {
          // Keys are in extra.keys (top-level keys is null)
          const keys = cache.extra?.keys ?? cache.keys ?? [];
          this.cacheKeysMap[cacheName] = Array.isArray(keys) ? keys : [];
          this.keysShownMap[cacheName] = true;
          this.keysLoadingMap[cacheName] = false;
        },
        error: () => {
          this.keysLoadingMap[cacheName] = false;
        }
      });
  }

  trackByKey(index: number, key: string): string {
    return key;
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }
}
